import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertCircle, Sparkles } from "lucide-react";
import { API_BASE } from "@/lib/api";

const TOKEN_KEY = "nv_admin_jwt";

/**
 * TrialBanner — thin banner at the top of Studio.
 * - Hidden for plan="paid"
 * - Shows days remaining during trial
 * - Turns amber during grace period
 * - Turns red + persistent when expired
 *
 * TrialPaywallModal — full-screen modal to show when a write returns 402.
 */
export function TrialBanner({ status, onUpgrade }) {
  if (!status || status.plan === "paid") return null;
  const isTrial = status.plan === "trial";
  const isGrace = status.plan === "trial_grace";
  const isExpired = status.plan === "expired" || status.plan === "canceled";

  // Suppress the banner during the calm part of the trial (>5 days remaining)
  if (isTrial && (status.days_remaining ?? 99) > 5) return null;

  const bg = isExpired ? "bg-[#8B2E1E] text-[#FAF7F0]" : isGrace ? "bg-[#F4B183] text-[#183D36]" : "bg-[#EFECE5] text-[#183D36]";
  const label = isExpired
    ? "Your free trial has ended — upgrade to keep sending follow-ups."
    : isGrace
    ? `Trial grace period — writes will be blocked in ${status.days_remaining} day${status.days_remaining === 1 ? "" : "s"}.`
    : `Trial ends in ${status.days_remaining} day${status.days_remaining === 1 ? "" : "s"}.`;

  return (
    <div className={`${bg} px-6 lg:px-10 py-2.5 flex items-center justify-between gap-3`} data-testid="studio-trial-banner">
      <div className="flex items-center gap-2 text-sm">
        <AlertCircle size={14} />
        <span data-testid="studio-trial-banner-text">{label}</span>
      </div>
      <Button
        size="sm"
        onClick={onUpgrade}
        className={`rounded-full ${isExpired ? "bg-[#FAF7F0] text-[#183D36] hover:bg-white" : "bg-[#183D36] text-[#FAF7F0] hover:bg-[#122e29]"}`}
        data-testid="studio-trial-banner-upgrade"
      >
        Upgrade
      </Button>
    </div>
  );
}

export function TrialPaywallModal({ open, onOpenChange, status }) {
  if (!status) return null;
  const expired = status.plan === "expired" || status.plan === "canceled";
  const trialEnd = status.trial_end ? new Date(status.trial_end) : null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl bg-[#FAF7F0] border-[#E5E0D8] max-w-md" data-testid="studio-paywall-modal">
        <DialogHeader>
          <div className="w-14 h-14 rounded-2xl bg-[#F4B183] flex items-center justify-center mb-3">
            <Sparkles className="text-[#183D36]" size={22} />
          </div>
          <DialogTitle className="font-heading text-2xl text-[#183D36]">
            {expired ? "Your trial has ended." : "Upgrade to continue."}
          </DialogTitle>
          <DialogDescription className="text-[#4A4A4A] text-base">
            {expired ? (
              <>
                Your 14-day trial ended{trialEnd ? ` on ${trialEnd.toLocaleDateString()}` : ""}. Upgrade to keep sending automated thank-yous, review requests, rebook nudges, and win-backs to your customers.
              </>
            ) : (
              <>
                You&apos;re trying to save something, but your trial has run out of writes. Upgrade to unlock full Studio access.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-2xl bg-white border border-[#E5E0D8] p-4 my-2">
          <ul className="text-sm text-[#252525] space-y-1.5">
            <li>· Unlimited customers &amp; appointments</li>
            <li>· All 4 message types (thank-you, review, rebook, win-back)</li>
            <li>· Square Appointments sync + real-time webhooks</li>
            <li>· AI-drafted templates in your voice</li>
          </ul>
        </div>
        <DialogFooter className="gap-2 sm:gap-2 sm:justify-between">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-full border border-[#183D36]/20"
            data-testid="studio-paywall-dismiss"
          >
            Not yet
          </Button>
          <Button
            onClick={() => { window.location.href = "/#pricing"; }}
            className="rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0] px-6"
            data-testid="studio-paywall-upgrade"
          >
            See plans
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Hook: fetches workspace status and returns { status, refresh, handle402 } */
export function useWorkspaceStatus() {
  const [status, setStatus] = useState(null);
  const [paywallOpen, setPaywallOpen] = useState(false);

  const refresh = async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) return;
      const r = await fetch(`${API_BASE}/product/workspace-status`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setStatus(await r.json());
    } catch { /* silent */ }
  };

  useEffect(() => { refresh(); }, []);

  const handle402 = (resp) => {
    // Call after any authed request; if 402, open the paywall
    if (resp && resp.status === 402) {
      setPaywallOpen(true);
      // Refresh status so banner reflects latest state
      refresh();
      return true;
    }
    return false;
  };

  return { status, paywallOpen, setPaywallOpen, refresh, handle402 };
}
